import { useTranslation } from "@sagot/i18n";
import { Card, CardHeader, CardTitle, Chip, EmptyState, Icon, Skeleton, type ChipProps } from "@sagot/ui";
import { ReassignMenu, type ReassignChat } from "./reassign-menu";
import { formatClock, tierLabel, type ReassignTarget, type WaitingChat } from "./wallboard-model";

interface WaitingListProps {
  waiting: readonly WaitingChat[];
  total: number;
  targets: readonly ReassignTarget[];
  canAssign: boolean;
  pending: boolean;
  onAssign: (chat: ReassignChat, target: ReassignTarget) => void;
}

interface Urgency {
  tone: NonNullable<ChipProps["tone"]>;
  icon: "shield-alert" | "alert-triangle" | "clock";
  key: string | null;
}

function urgencyOf(chat: WaitingChat): Urgency {
  if (chat.safety) return { tone: "danger", icon: "shield-alert", key: "wallboard.board.waiting.safety" };
  if (chat.priority === "urgent") return { tone: "danger", icon: "alert-triangle", key: "wallboard.board.waiting.priorityUrgent" };
  if (chat.priority === "high") return { tone: "warning", icon: "alert-triangle", key: "wallboard.board.waiting.priorityHigh" };
  return { tone: "muted", icon: "clock", key: null };
}

function WaitingRow({ chat, targets, canAssign, pending, onAssign }: { chat: WaitingChat; targets: readonly ReassignTarget[]; canAssign: boolean; pending: boolean; onAssign: WaitingListProps["onAssign"] }) {
  const { t } = useTranslation();
  const urgency = urgencyOf(chat);
  const title = chat.title || t("wallboard.board.waiting.untitled");
  const details = [chat.team, chat.tier ? tierLabel(chat.tier, "") : null, t("wallboard.board.waiting.waited", { time: formatClock(chat.waiting_s) })].filter(Boolean);
  const target: ReassignChat | null = chat.conversation_id ? { id: chat.conversation_id, title } : null;
  return (
    <li className="flex items-start gap-3 border-b border-line px-4 py-3 last:border-b-0" data-testid="waiting-row" data-priority={chat.priority} data-safety={chat.safety}>
      <span className="mt-0.5 shrink-0">
        <Chip tone={urgency.tone} icon={urgency.icon}>
          {urgency.key ? t(urgency.key) : <span className="sr-only">{chat.priority}</span>}
        </Chip>
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-fg" data-testid="waiting-title">{title}</p>
        <p className="truncate text-xs text-fg-muted">{details.join(" · ")}</p>
      </div>
      {canAssign && target ? (
        <ReassignMenu
          chats={[target]}
          targets={targets}
          label={t("wallboard.board.waiting.assign")}
          ariaLabel={t("wallboard.board.waiting.assignLabel", { title })}
          icon="user-plus"
          pending={pending}
          onPick={onAssign}
          testId={`assign-${target.id}`}
        />
      ) : null}
    </li>
  );
}

export function WaitingSkeleton() {
  return (
    <div className="space-y-3 p-4" data-testid="wallboard-waiting-loading">
      {[0, 1, 2].map((row) => <Skeleton key={row} className="h-12 w-full" />)}
    </div>
  );
}

/** "Waiting now": the next chats to be taken, safety cases first, then by priority and wait. */
export function WaitingList({ waiting, total, targets, canAssign, pending, onAssign }: WaitingListProps) {
  const { t } = useTranslation();
  const more = Math.max(0, total - waiting.length);
  return (
    <Card data-testid="wallboard-waiting">
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>{t("wallboard.board.waiting.title")}</CardTitle>
          <p className="mt-0.5 text-xs text-fg-muted">{t("wallboard.board.waiting.subtitle")}</p>
        </div>
      </CardHeader>
      {waiting.length === 0 ? (
        <EmptyState icon="check-circle" title={t("wallboard.board.waiting.emptyTitle")} description={t("wallboard.board.waiting.emptyBody")} headingLevel={3} />
      ) : (
        <ul aria-label={t("wallboard.board.waiting.listLabel")}>
          {waiting.map((chat, index) => (
            <WaitingRow key={chat.conversation_id ?? `${String(index)}-${chat.title}`} chat={chat} targets={targets} canAssign={canAssign} pending={pending} onAssign={onAssign} />
          ))}
        </ul>
      )}
      {more > 0 ? (
        <p className="flex items-center gap-2 border-t border-line px-4 py-3 text-xs font-semibold text-fg-muted" data-testid="waiting-more">
          <Icon name="inbox" className="size-4" />
          {t("wallboard.board.waiting.more", { count: more })}
        </p>
      ) : null}
    </Card>
  );
}
